import React from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import SEOHead from '../components/seo/SEOHead';
import Breadcrumb from '../components/seo/Breadcrumb';
import { PATENTS } from '../data/patents';
import { isEnglishPath } from '../utils/i18nRoutes';

const ALTERNATES = [
  { hreflang: 'es', href: '/patentes' },
  { hreflang: 'en', href: '/en/patentes' },
  { hreflang: 'x-default', href: '/patentes' },
];

const TEXT = {
  es: {
    seoTitle: 'Patentes y propiedad industrial | Valoriza',
    seoDescription: 'Diecisiete títulos de propiedad industrial de Valoriza: patentes, modelos de utilidad y un diseño industrial. Del secado de lixiviados al C2C.',
    jsonLdName: 'Patentes y propiedad industrial de Valoriza',
    jsonLdDesc: 'Diecisiete títulos de propiedad industrial de Valoriza: patentes, modelos de utilidad y un diseño industrial.',
    breadcrumb: [{ label: 'Inicio', to: '/' }, { label: 'Patentes' }],
    tag: 'Propiedad industrial',
    h1a: 'Patentes y propiedad',
    h1b: 'industrial',
    lead: 'Diecisiete títulos de propiedad industrial de Valoriza: patentes, modelos de utilidad y un diseño industrial. Del secado de lixiviados al C2C.',
    body: 'En Valoriza, la innovación se protege. Estos títulos son el resultado de años de trabajo en campo: no es un portafolio de laboratorio, sino la formalización de nuestra experiencia y del valor que aportamos en la calle.',
    bodyStrong: 'Lo que protegemos, porque lo operamos.',
    colN: '#',
    colTitle: 'Título público',
    colNumber: 'Número',
    linksTitle: 'Enlaces',
    linkCanaria: 'Canaria+: nuestro wearable patentado',
    linkProjects: 'Proyectos I+D+i relacionados',
    cta: '¿Tienes una idea que proteger?',
    canariaPath: '/canaria',
    projectsPath: '/proyectos',
    contactPath: '/contacto',
  },
  en: {
    seoTitle: 'Patents and industrial property | Valoriza',
    seoDescription: 'Seventeen industrial property titles at Valoriza: patents, utility models and one industrial design. From leachate drying to C2C.',
    jsonLdName: 'Valoriza\u2019s patents and industrial property',
    jsonLdDesc: 'Seventeen industrial property titles at Valoriza: patents, utility models and one industrial design.',
    breadcrumb: [{ label: 'Home', to: '/en' }, { label: 'Patents' }],
    tag: 'Industrial property',
    h1a: 'Patents and industrial',
    h1b: 'property',
    lead: 'Seventeen industrial property titles at Valoriza: patents, utility models and one industrial design. From leachate drying to C2C.',
    body: 'At Valoriza, innovation gets protected. These titles are the result of years of work in the field: not a lab portfolio, but the formalization of our experience and the value we bring to the street.',
    bodyStrong: 'What we protect, because we operate it.',
    colN: '#',
    colTitle: 'Public title',
    colNumber: 'Number',
    linksTitle: 'Links',
    linkCanaria: 'Canaria+: our patented wearable',
    linkProjects: 'Related R&D projects',
    cta: 'Do you have an idea to protect?',
    canariaPath: '/en/canaria',
    projectsPath: '/en/projects',
    contactPath: '/en/contact',
  },
};

export default function PatentesPage() {
  const { pathname } = useLocation();
  const locale = isEnglishPath(pathname) ? 'en' : 'es';
  const t = TEXT[locale];
  const path = locale === 'en' ? '/en/patentes' : '/patentes';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: t.jsonLdName,
    description: t.jsonLdDesc,
    url: `https://innovation.valorizasm.com${path}`,
  };

  return (
    <>
      <SEOHead
        title={t.seoTitle}
        description={t.seoDescription}
        path={path}
        locale={locale}
        alternates={ALTERNATES}
        jsonLd={[jsonLd]}
      />
      <div className="pt-32 pb-24 v-container" data-testid="patentes-page">
        <Breadcrumb items={t.breadcrumb} />

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl mb-12">
          <span className="v-tag green mb-6 inline-flex">{t.tag}</span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-black dark:text-white/95 mb-6 tracking-tight leading-tight" data-testid="patentes-h1">
            {t.h1a} <span style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', color: '#8DC21F' }}>{t.h1b}</span>
          </h1>
          <p className="text-base sm:text-lg text-gray-700 dark:text-white/65 leading-relaxed mb-4">
            {t.lead}
          </p>
          <p className="text-sm sm:text-base text-gray-700 dark:text-white/65 leading-relaxed">
            {t.body} <strong className="text-black dark:text-white">{t.bodyStrong}</strong>
          </p>
        </motion.div>

        <div className="overflow-x-auto liquid-glass rounded-2xl mb-12">
          <table className="w-full text-sm" data-testid="patentes-table">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--v-border)' }}>
                <th className="text-left p-4 text-[11px] uppercase tracking-wide text-gray-500 dark:text-white/40">{t.colN}</th>
                <th className="text-left p-4 text-[11px] uppercase tracking-wide text-gray-500 dark:text-white/40">{t.colTitle}</th>
                <th className="text-left p-4 text-[11px] uppercase tracking-wide text-gray-500 dark:text-white/40">{t.colNumber}</th>
              </tr>
            </thead>
            <tbody>
              {PATENTS.map((p) => (
                <tr key={p.n} style={{ borderBottom: '1px solid var(--v-border)' }}>
                  <td className="p-4 text-gray-500 dark:text-white/40">{p.n}</td>
                  <td className="p-4 font-medium text-black dark:text-white/85">{locale === 'en' ? p.tituloEn : p.titulo}</td>
                  <td className="p-4 text-gray-600 dark:text-white/55 font-mono text-xs">{p.numero}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="glass-card p-6 max-w-2xl mb-10">
          <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-white/40 mb-3">{t.linksTitle}</h3>
          <div className="flex flex-col gap-2.5 text-sm">
            <Link to={t.canariaPath} className="hover:text-[#8DC21F] transition-colors">{t.linkCanaria}</Link>
            <Link to={t.projectsPath} className="hover:text-[#8DC21F] transition-colors">{t.linkProjects}</Link>
          </div>
        </div>

        <Link to={t.contactPath} className="btn-primary inline-flex items-center gap-2" data-testid="patentes-cta-contact">
          {t.cta} <ArrowRight size={15} />
        </Link>
      </div>
    </>
  );
}
